import React, { useRef, useState } from "react";
import { Camera, Loader2, Upload, X } from "lucide-react";
import { uploadAvatar } from "../lib/api";
import { toast } from "sonner";

const abs = (u) => u;  // same-origin, no prefix needed

/**
 * Avatar/logo picker that replaces the old glyph carousel.
 * - Drag-and-drop OR click to browse.
 * - Uses the public `/api/uploads/avatar` endpoint (5 MB image, no auth required).
 * - Shows a preview with a remove button.
 */
export const AvatarUploader = ({ value, onChange, fallback = "◆", size = 88, testId = "avatar-uploader" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [hovering, setHovering] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only images are allowed (jpg, png, webp).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5 MB).");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadAvatar(file);
      onChange(res.url);
      toast.success("Avatar uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      data-testid={testId}
      onDragOver={(e) => { e.preventDefault(); setHovering(true); }}
      onDragLeave={() => setHovering(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHovering(false);
        const file = Array.from(e.dataTransfer?.files || [])[0];
        if (file) doUpload(file);
      }}
      className="flex items-center gap-4"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => doUpload(e.target.files?.[0])}
      />
      <button
        type="button"
        data-testid={`${testId}-zone`}
        onClick={() => inputRef.current?.click()}
        style={{ width: size, height: size }}
        className={`relative rounded-2xl overflow-hidden border-2 ${
          value ? "border-[#00ff88]/40" : hovering ? "border-[#00ff88]" : "border-dashed border-[#15152a]"
        } bg-[#06060c] flex items-center justify-center shrink-0 transition-colors group`}
      >
        {value ? (
          <>
            <img src={abs(value)} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Camera size={18} className="text-white" />
            </div>
          </>
        ) : uploading ? (
          <Loader2 size={22} className="text-[#00ff88] animate-spin" />
        ) : (
          <span className="text-3xl text-[#00ff88]">{fallback}</span>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-medium">Upload your logo</div>
        <div className="text-[11px] text-[#606070] leading-relaxed">
          Drag & drop an image here, or click to browse. JPG · PNG · WebP · up to 5 MB.
        </div>
        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            data-testid={`${testId}-browse`}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 text-xs text-[#00ff88] hover:underline"
          >
            <Upload size={11} /> Browse
          </button>
          {value && (
            <button
              type="button"
              data-testid={`${testId}-remove`}
              onClick={() => onChange(null)}
              className="inline-flex items-center gap-1.5 text-xs text-[#ff4444] hover:underline"
            >
              <X size={11} /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvatarUploader;
